import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Pagination } from './ui/pagination';
import { 
  Plus, 
  Search, 
  Eye, 
  Edit, 
  Trash2, 
  FileText, 
  Package,
  Filter,
  Download,
  Clock,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Play,
  Pause,
  Square,
  Loader2,
  RefreshCw
} from 'lucide-react';
import { workOrderApiService, WorkOrder, WorkOrderStatus, WorkOrderPriority } from '../services/workOrderApi';
import { useAuth } from '../contexts/AuthContext';
import { useBranch } from '../contexts/BranchContext';

// Using the WorkOrder interface from the API service

const WorkOrderListingPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const { user } = useAuth();
  const { selectedBranch } = useBranch();
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  
  // API state
  const [workOrders, setWorkOrders] = useState<WorkOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const isFetchingRef = useRef(false);
  
  // Server-side pagination state
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [serverCurrentPage, setServerCurrentPage] = useState(1);
  
  // Filters removed as requested

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch work orders from API with server-side pagination
  const fetchWorkOrders = useCallback(async (page: number = 1, searchQuery: string = '', filters: any = {}) => {
    // Prevent multiple simultaneous calls using ref
    if (isFetchingRef.current) {
      // console.log('🚫 Skipping fetch - already fetching (ref)');
      return;
    }
    
    try {
      isFetchingRef.current = true;
      setIsFetching(true);
      setLoading(true);
      setError(null);
      
      // Resolve active branch id strictly from header selection
      const activeBranchId = selectedBranch;
      const finalFilters = { ...filters };
      if (activeBranchId) {
        finalFilters.branchId = activeBranchId;
      }
      
      // Add workOrderNumber to filters if search query is provided
      if (searchQuery && searchQuery.trim()) {
        finalFilters.workOrderNumber = searchQuery.trim();
      }

      // console.log('🔄 Fetching work orders - page:', page, 'search:', searchQuery, 'branch:', activeBranchId);
      const response = await workOrderApiService.getWorkOrdersWithPagination(page, pageSize, finalFilters);
      
      if (response.success && response.data) {
        // Extract work orders from the nested structure
        const workOrdersData = (response.data as any).content || response.data;
        setWorkOrders(workOrdersData);
        setServerCurrentPage(page);
        
        // Handle pagination info from the response structure
        if ((response.data as any).totalElements !== undefined) {
          setTotalCount((response.data as any).totalElements);
          setTotalPages((response.data as any).totalPages);
        } else if (response.pagination) {
          setTotalCount(response.pagination.totalCount);
          setTotalPages(response.pagination.totalPages);
        } else {
          setTotalCount(workOrdersData.length);
          setTotalPages(1);
        }
      }
    } catch (error) {
      // console.error('Error loading work orders:', error);
      setError('Failed to load work orders');
      toast.error('Failed to load work orders');
    } finally {
      setLoading(false);
      setIsFetching(false);
      isFetchingRef.current = false;
    }
  }, [pageSize, selectedBranch, user?.defaultBranchId]);

  // Load work orders when search term changes or on initial mount
  useEffect(() => {
    // console.log('🔄 useEffect triggered - debouncedSearchTerm:', debouncedSearchTerm, 'selectedBranch:', selectedBranch, 'defaultBranchId:', user?.defaultBranchId);
    
    // Don't trigger if already fetching using ref
    if (isFetchingRef.current) {
      // console.log('🚫 useEffect skipped - already fetching (ref)');
      return;
    }
    
    // Wait until header branchId (selectedBranch) is available
    // On refresh, selectedBranch might be null initially, then set to defaultBranchId
    // We need to wait for it to be properly set (not null/undefined)
    if (selectedBranch === null || selectedBranch === undefined) {
      // console.log('⏳ Waiting for selectedBranch from header before fetching work orders');
      return;
    }
    
    // console.log('✅ selectedBranch is available:', selectedBranch, 'proceeding with fetch');
    fetchWorkOrders(1, debouncedSearchTerm);
  }, [fetchWorkOrders, debouncedSearchTerm, selectedBranch, user?.defaultBranchId]);

  // Use work orders directly from API (server-side search is now supported)
  const filteredWorkOrders = useMemo(() => {
    // Ensure workOrders is always an array
    return Array.isArray(workOrders) ? workOrders : [];
  }, [workOrders]);

  const getStatusBadge = (status: string) => {
    // Normalize status to handle both enum format (IN_PROGRESS) and display format (In Progress)
    // Replace spaces and underscores, then convert to uppercase for consistent lookup
    const normalizedStatus = status?.replace(/\s+/g, '_').toUpperCase() || '';
    
    const statusConfig: Record<string, { color: string; icon: any; label: string }> = {
      'PENDING': { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: Clock, label: 'Pending' },
      'IN_PROGRESS': { color: 'bg-blue-100 text-blue-800 border-blue-200', icon: Play, label: 'In Progress' },
      'ON_HOLD': { color: 'bg-orange-100 text-orange-800 border-orange-200', icon: Pause, label: 'On Hold' },
      'COMPLETED': { color: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle2, label: 'Completed' },
      'CANCELLED': { color: 'bg-red-100 text-red-800 border-red-200', icon: XCircle, label: 'Cancelled' }
    };

    const config = statusConfig[normalizedStatus] || statusConfig['PENDING'];
    const Icon = config.icon;

    return (
      <Badge className={`${config.color} border font-medium`}>
        <Icon className="h-3 w-3 mr-1" />
        {config.label}
      </Badge>
    );
  };


  const getProgressPercentage = (completed: number, total: number) => {
    if (total === 0) return 0;
    return Math.round((completed / total) * 100);
  };

  // Handle refresh
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchWorkOrders(serverCurrentPage, debouncedSearchTerm);
    setRefreshing(false);
  };

  const handleCreate = () => {
    navigate('/work-orders/new');
  };

  const handleView = (id: number) => {
    navigate(`/work-orders/${id}/view`);
  };

  const handleEdit = (id: number) => {
    navigate(`/work-orders/${id}/edit`);
  };

  const handleDelete = (id: number) => {
    const workOrder = workOrders.find(wo => wo.id === id);
    if (window.confirm(`Are you sure you want to delete Work Order ${workOrder?.workOrderNumber}?`)) {
      setWorkOrders(prev => prev.filter(wo => wo.id !== id));
      toast.success('Work Order deleted successfully');
    }
  };

  const handleStatusChange = (id: number, newStatus: WorkOrder['status']) => {
    setWorkOrders(prev => prev.map(wo => 
      wo.id === id ? { ...wo, status: newStatus, updatedAt: new Date().toISOString().split('T')[0] } : wo
    ));
    toast.success(`Work Order status updated to ${newStatus}`);
  };

  // Show error if there's an error
  if (error && !loading) {
    return (
      <div className="space-y-6">
        <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
          <CardContent className="p-6 text-center">
            <div className="text-red-600 mb-4">
              <FileText className="h-12 w-12 mx-auto mb-3" />
              <p className="text-lg font-semibold">Error Loading Work Orders</p>
              <p className="text-sm text-gray-600 mt-2">{error}</p>
            </div>
            <Button onClick={handleRefresh} className="bg-primary-600 hover:bg-primary-700">
              <RefreshCw className="h-4 w-4 mr-2" />
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
        <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-lg bg-primary-200 flex items-center justify-center">
                <FileText className="h-5 w-5 text-primary-700" />
              </div>
              <div>
                <CardTitle className="text-xl text-gray-900">Work Order Management</CardTitle>
                <p className="text-gray-600 text-sm">Manage and track production work orders</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={handleRefresh}
                variant="outline"
                size="sm"
                disabled={loading}
                className="border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
              </Button>
              <Button
                onClick={handleCreate}
                className="bg-primary-500 text-gray-700 hover:bg-gray-50 shadow-lg"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Work Order
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {/* Search and Filters */}
          <div className="flex items-center gap-4 mb-6">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search work orders..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 border-primary-200 focus:border-primary-400 focus:ring-primary-400"
              />
            </div>
            <div className="text-sm text-gray-600">
              {Array.isArray(filteredWorkOrders) ? filteredWorkOrders.length : 0} of {totalCount} work orders
            </div>
          </div>

          {/* Filters removed as requested */}
        </CardContent>
      </Card>

      {/* Work Orders Table */}
      <div className="border border-primary-200 rounded-lg overflow-hidden bg-white">
        {loading ? (
          <div className="p-8 text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-3 text-primary-600" />
            <p className="text-gray-600">Loading work orders...</p>
          </div>
        ) : !Array.isArray(filteredWorkOrders) || filteredWorkOrders.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">No work orders found</p>
            <p className="text-gray-400">Create your first work order to get started</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-gradient-to-r from-primary-50 to-primary-100/50">
                <TableHead>Work Order</TableHead>
                <TableHead>Part Info</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Quantity</TableHead>
                <TableHead>Progress</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Array.isArray(filteredWorkOrders) && filteredWorkOrders.map((workOrder) => (
                <TableRow key={workOrder.id} className="hover:bg-primary-50/50">
                      <TableCell>
                        <div className="space-y-1">
                          <div className="font-medium text-gray-900">{workOrder.workOrderNumber}</div>
                          <div className="text-xs text-gray-500">BOM: {(workOrder as any).bomName || 'N/A'}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="font-medium">{(workOrder as any).endgoodName || 'N/A'}</div>
                          <div className="text-sm text-gray-500">{(workOrder as any).endgoodSku || 'N/A'}</div>
                          <div className="text-xs text-gray-400">SO: {(workOrder as any).salesOrderNumber || 'N/A'}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="font-medium">{workOrder.customerName}</span>
                      </TableCell>
                      <TableCell>
                        <div className="text-center">
                          <div className="font-medium">{workOrder.quantity}</div>
                          <div className="text-xs text-gray-500">
                            {workOrder.completedQuantity || 0}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="w-20">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-gray-200 rounded-full h-2">
                              <div 
                                className="bg-gradient-to-r from-primary-500 to-primary-600 h-2 rounded-full transition-all duration-300"
                                style={{ width: `${getProgressPercentage(workOrder.completedQuantity, workOrder.quantity)}%` }}
                              ></div>
                            </div>
                            <span className="text-xs font-medium text-gray-600">
                              {getProgressPercentage(workOrder.completedQuantity, workOrder.quantity)}%
                            </span>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(workOrder.status)}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleView(workOrder.id)}
                            className="h-8 w-8 p-0 border-primary-200 hover:bg-primary-50 hover:border-primary-300 text-primary-600"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEdit(workOrder.id)}
                            className="h-8 w-8 p-0 border-primary-200 hover:bg-primary-50 hover:border-primary-300 text-primary-600"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete(workOrder.id)}
                            className="h-8 w-8 p-0 border-primary-200 hover:bg-primary-50 hover:border-primary-300 text-primary-600"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Pagination */}
      {Array.isArray(filteredWorkOrders) && filteredWorkOrders.length > 0 && totalPages > 1 && (
        <div className="flex justify-center mt-6">
          <Pagination
            currentPage={serverCurrentPage}
            totalPages={totalPages}
            onPageChange={(page) => {
              fetchWorkOrders(page, debouncedSearchTerm);
            }}
          />
        </div>
      )}
    </div>
  );
};

export { WorkOrderListingPage };
